import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Text } from "@geist-ui/core";
import { Flex } from "@radix-ui/themes";
import { AlertTriangle, ArrowDown, ArrowUp, Plus, Users, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/shared/dialog";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import SharedSelect from "@/components/shared/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Topic } from "@/v2/entities/topic/Topic.model";
import {
  useAuiConfigurations,
  useDeleteAuiConfiguration,
  useUpsertAuiConfiguration,
} from "@/v2/entities/aui-configuration/AuiConfiguration.api";
import { AuiConfiguration } from "@/v2/entities/aui-configuration/AuiConfiguration.model";

import styles from "./styles.module.scss";

type AudienceOperator = "equals" | "contains" | "gte" | "lte";

interface AudienceCondition {
  id: string;
  field: string;
  operator: AudienceOperator;
  value: string;
}

interface RolloutGroup {
  groupId: string;
  conditions: AudienceCondition[];
  /** Apollo traffic percentage per topic id. */
  percentages: Record<number, number>;
}

/** Group id used for rows saved before filter groups existed. */
const DEFAULT_GROUP_ID = "default";

/** Tenant-context keys that can gate the rollout, with human labels. */
const AUDIENCE_FIELDS: {
  value: string;
  label: string;
  kind: "boolean" | "text" | "number";
}[] = [
  { value: "IS_LT", label: "Low tier", kind: "boolean" },
  { value: "Is_Free", label: "Free plan", kind: "boolean" },
  { value: "Is_HT", label: "High tier", kind: "boolean" },
  { value: "package", label: "Package", kind: "text" },
  { value: "Product_Line", label: "Product line", kind: "text" },
  { value: "total_acv", label: "Account ACV", kind: "number" },
];

const OPERATORS_BY_KIND: Record<
  "boolean" | "text" | "number",
  { value: AudienceOperator; label: string }[]
> = {
  boolean: [{ value: "equals", label: "is" }],
  text: [
    { value: "equals", label: "is" },
    { value: "contains", label: "contains (case-sensitive)" },
  ],
  number: [
    { value: "gte", label: "is at least" },
    { value: "lte", label: "is at most" },
  ],
};

const BOOLEAN_OPTIONS = [
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

function getFieldKind(field: string) {
  return AUDIENCE_FIELDS.find((f) => f.value === field)?.kind ?? "text";
}

const isInvalidNumberCondition = (condition: AudienceCondition) =>
  getFieldKind(condition.field) === "number" &&
  (condition.value.trim() === "" || !Number.isFinite(Number(condition.value)));

/**
 * Non-exact operators are stored in the filter key as `<field>:<operator>` (e.g. `total_acv:gte`),
 * which is how session-tracker parses them. Plain keys are exact matches.
 */
function toConditions(filters: Record<string, string> | undefined) {
  if (!filters) return [] as AudienceCondition[];
  return Object.entries(filters).map(([key, value], index) => {
    const operatorMatch = /^(.+):(contains|gte|lte)$/.exec(key);
    return {
      id: `cond-${key}-${index}`,
      field: operatorMatch ? operatorMatch[1] : key,
      operator: (operatorMatch?.[2] ?? "equals") as AudienceOperator,
      value,
    };
  });
}

/** Collapses audience conditions back into the filters map sent to the API. */
function toFilters(conditions: AudienceCondition[]) {
  const filters: Record<string, string> = {};
  for (const condition of conditions) {
    if (!condition.field || !condition.value) continue;
    const key =
      condition.operator === "equals"
        ? condition.field
        : `${condition.field}:${condition.operator}`;
    filters[key] = condition.value;
  }
  return filters;
}

const configsForSimulator = (
  configs: AuiConfiguration[] | undefined,
  simulatorId: number
) =>
  configs?.filter((c) => String(c.simulatorId) === String(simulatorId)) ?? [];

const groupIdOf = (config: AuiConfiguration) => config.groupId || DEFAULT_GROUP_ID;

/**
 * Rebuilds the editable groups from the stored rows. Rows are one per group+topic, so they get
 * folded back into a group per distinct groupId, ordered by the priority the backend matches on.
 */
function toGroups(
  configs: AuiConfiguration[] | undefined,
  simulatorId: number,
  topics: Topic[]
): RolloutGroup[] {
  const mine = configsForSimulator(configs, simulatorId);
  if (mine.length === 0) {
    return [];
  }

  const byGroup = new Map<string, AuiConfiguration[]>();
  for (const config of mine) {
    const key = groupIdOf(config);
    byGroup.set(key, [...(byGroup.get(key) ?? []), config]);
  }

  return [...byGroup.entries()]
    .sort(
      ([, a], [, b]) =>
        (a[0].priority ?? Number.MAX_SAFE_INTEGER) -
        (b[0].priority ?? Number.MAX_SAFE_INTEGER)
    )
    .map(([groupId, rows]) => {
      const percentages: Record<number, number> = {};
      for (const topic of topics) {
        percentages[topic.id] =
          rows.find((r) => r.topic === topic.topic)?.percentage ?? 0;
      }
      return {
        groupId,
        conditions: toConditions(rows.find((r) => r.filters)?.filters),
        percentages,
      };
    });
}

const sameFilters = (
  a: Record<string, string> | undefined,
  b: Record<string, string> | undefined
) =>
  JSON.stringify(Object.entries(a ?? {}).sort()) ===
  JSON.stringify(Object.entries(b ?? {}).sort());

/**
 * A group is unreachable when a group above it matches everything it matches, i.e. the upper
 * group's filters are a subset of this one's. Matching stops at the first hit, so ordering
 * mistakes are silent without this check.
 */
function findShadowingGroup(groups: RolloutGroup[], index: number) {
  const own = toFilters(groups[index].conditions);
  return groups.slice(0, index).find((above) =>
    Object.entries(toFilters(above.conditions)).every(
      ([field, value]) => own[field] === value
    )
  );
}

const emptyGroup = (topics: Topic[], groupId: string): RolloutGroup => ({
  groupId,
  conditions: [],
  percentages: Object.fromEntries(topics.map((t) => [t.id, 0])),
});

/**
 * Row id for a group's topic. The default group keeps the pre-groups id so existing rollouts are
 * updated in place rather than duplicated.
 */
const buildConfigId = (
  simulatorId: number,
  groupId: string,
  topicId: number
) =>
  groupId === DEFAULT_GROUP_ID
    ? `aui-rollout-${simulatorId}-${topicId}`
    : `aui-rollout-${simulatorId}-${groupId}-${topicId}`;

export function useApolloPercentageByTopic(
  simulatorId: number | undefined,
  enabled = true
) {
  const { data: configurations } = useAuiConfigurations(
    enabled && Boolean(simulatorId)
  );

  return useMemo(() => {
    const map = new Map<string, string>();
    if (!simulatorId || !configurations) return map;

    const byTopic = new Map<string, number[]>();
    for (const config of configsForSimulator(configurations, simulatorId)) {
      byTopic.set(config.topic, [
        ...(byTopic.get(config.topic) ?? []),
        config.percentage,
      ]);
    }
    // A topic can now have one percentage per filter group, so show the spread.
    for (const [topic, percentages] of byTopic) {
      const min = Math.min(...percentages);
      const max = Math.max(...percentages);
      map.set(topic, min === max ? `${max}%` : `${min}–${max}%`);
    }
    return map;
  }, [configurations, simulatorId]);
}

interface ApolloRolloutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  simulatorId: number;
  topics: Topic[];
}

export const ApolloRolloutDialog: React.FC<ApolloRolloutDialogProps> = ({
  open,
  onOpenChange,
  simulatorId,
  topics,
}) => {
  const { data: configurations, isLoading: isLoadingConfigs } =
    useAuiConfigurations(open);
  const { mutateAsync: upsertConfiguration } = useUpsertAuiConfiguration();
  const { mutateAsync: deleteConfiguration } = useDeleteAuiConfiguration();

  const [groups, setGroups] = useState<RolloutGroup[]>([]);
  const [removedGroupIds, setRemovedGroupIds] = useState<string[]>([]);
  const [isSavingAll, setIsSavingAll] = useState(false);

  useEffect(() => {
    if (!open) return;
    const existing = toGroups(configurations, simulatorId, topics);
    setGroups(
      existing.length > 0 ? existing : [emptyGroup(topics, DEFAULT_GROUP_ID)]
    );
    setRemovedGroupIds([]);
  }, [topics, configurations, simulatorId, open]);

  const updateGroup = useCallback(
    (groupId: string, updates: Partial<RolloutGroup>) => {
      setGroups((prev) =>
        prev.map((group) =>
          group.groupId === groupId ? { ...group, ...updates } : group
        )
      );
    },
    []
  );

  const handlePercentageChange = useCallback(
    (groupId: string, topicId: number, value: number) => {
      const clamped = Math.max(0, Math.min(100, Math.round(value)));
      setGroups((prev) =>
        prev.map((group) =>
          group.groupId === groupId
            ? {
                ...group,
                percentages: { ...group.percentages, [topicId]: clamped },
              }
            : group
        )
      );
    },
    []
  );

  const addGroup = useCallback(() => {
    setGroups((prev) => [
      ...prev,
      emptyGroup(topics, `group-${Date.now()}`),
    ]);
  }, [topics]);

  const removeGroup = useCallback((groupId: string) => {
    setGroups((prev) => prev.filter((group) => group.groupId !== groupId));
    setRemovedGroupIds((prev) => [...prev, groupId]);
  }, []);

  /** Order is precedence: the first group whose filters match a conversation wins. */
  const moveGroup = useCallback((index: number, direction: -1 | 1) => {
    setGroups((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }, []);

  const addCondition = useCallback(
    (group: RolloutGroup) => {
      updateGroup(group.groupId, {
        conditions: [
          ...group.conditions,
          {
            id: `cond-${Date.now()}`,
            field: "IS_LT",
            operator: "equals",
            value: "true",
          },
        ],
      });
    },
    [updateGroup]
  );

  const updateCondition = useCallback(
    (
      group: RolloutGroup,
      conditionId: string,
      updates: Partial<AudienceCondition>
    ) => {
      updateGroup(group.groupId, {
        conditions: group.conditions.map((condition) =>
          condition.id === conditionId
            ? { ...condition, ...updates }
            : condition
        ),
      });
    },
    [updateGroup]
  );

  const removeCondition = useCallback(
    (group: RolloutGroup, conditionId: string) => {
      updateGroup(group.groupId, {
        conditions: group.conditions.filter((c) => c.id !== conditionId),
      });
    },
    [updateGroup]
  );

  const sortedTopics = useMemo(
    () => [...topics].sort((a, b) => a.topic.localeCompare(b.topic)),
    [topics]
  );

  const existingById = useMemo(() => {
    const map = new Map<string, AuiConfiguration>();
    for (const config of configsForSimulator(configurations, simulatorId)) {
      map.set(config.id, config);
    }
    return map;
  }, [configurations, simulatorId]);

  /**
   * Only the rows that differ from what was loaded. Saving every group x topic on each Save would
   * be dozens of writes, and each one invalidates the server's rollout cache and re-scans the table.
   * Compares raw groupId/priority so rows stored before groups existed get stamped exactly once.
   */
  const changedRows = useMemo(
    () =>
      groups.flatMap((group, index) => {
        const filters = toFilters(group.conditions);
        return topics
          .map((topic) => ({
            id: buildConfigId(simulatorId, group.groupId, topic.id),
            payload: {
              simulatorId: String(simulatorId),
              topic: topic.topic,
              percentage: group.percentages[topic.id] ?? 0,
              filters,
              groupId: group.groupId,
              priority: index,
            },
          }))
          .filter(({ id, payload }) => {
            const existing = existingById.get(id);
            return (
              !existing ||
              existing.percentage !== payload.percentage ||
              existing.groupId !== payload.groupId ||
              existing.priority !== payload.priority ||
              !sameFilters(existing.filters, payload.filters)
            );
          });
      }),
    [groups, topics, simulatorId, existingById]
  );

  // An empty or non-numeric ACV bound would drop the condition and widen the group, so block saving
  const hasInvalidNumberCondition = groups.some((group) =>
    group.conditions.some(isInvalidNumberCondition)
  );

  const handleSaveAll = useCallback(async () => {
    setIsSavingAll(true);
    try {
      const writes = changedRows.map(({ id, payload }) =>
        upsertConfiguration({ id, payload })
      );

      // Delete the rows that actually exist rather than recomputing ids, so rows left behind by a
      // since-deleted topic go too and cannot resurrect the group on the next open.
      const deletes = removedGroupIds.flatMap((groupId) =>
        configsForSimulator(configurations, simulatorId)
          .filter((config) => groupIdOf(config) === groupId)
          .map((config) => deleteConfiguration(config.id))
      );

      const results = await Promise.allSettled([...writes, ...deletes]);
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) {
        toast.success("Saved Apollo rollout configuration");
        onOpenChange(false);
      } else {
        toast.error(
          `Failed to save ${failed} of ${results.length} configuration(s)`
        );
      }
    } finally {
      setIsSavingAll(false);
    }
  }, [
    changedRows,
    removedGroupIds,
    configurations,
    simulatorId,
    upsertConfiguration,
    deleteConfiguration,
    onOpenChange,
  ]);

  const audienceLabel = (group: RolloutGroup) => {
    const active = group.conditions.filter((c) => c.field && c.value);
    if (active.length === 0) return "All conversations";
    return `${active.length} filter${active.length > 1 ? "s" : ""} applied`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="large">
        <Flex justify="between" align="center" className={styles.rolloutHeader}>
          <div>
            <Dialog.Title>Apollo Rollout</Dialog.Title>
            <Text small style={{ color: "var(--gray-11)" }}>
              Each group targets its own audience with its own topic
              percentages. A conversation is matched against the groups top to
              bottom and uses the first one it matches. The rest of the traffic
              continues to Quack. Put narrower audiences above broader ones,
              otherwise the broader group catches their conversations first.
            </Text>
          </div>
          <Dialog.Close />
        </Flex>

        <div className={styles.rolloutBody}>
          {groups.map((group, index) => {
            const shadowedBy = findShadowingGroup(groups, index);
            return (
            <div
              key={group.groupId}
              className="mb-6 rounded-lg border border-[var(--gray-4)] p-4"
            >
              <div className="mb-3 flex items-center gap-3">
                <span
                  className="inline-flex items-center rounded-md bg-[var(--gray-3)] px-2 py-1 text-xs font-medium text-[var(--gray-11)]"
                  title="Groups are checked top to bottom; the first match wins"
                >
                  Priority {index + 1}
                </span>
                <span className="inline-flex items-center gap-2 rounded-md border border-[var(--gray-4)] px-3 py-1.5 text-sm text-[var(--gray-11)]">
                  <Users size={16} />
                  {audienceLabel(group)}
                </span>
                <Button
                  variant="outline"
                  className={styles.actionButton}
                  onClick={() => addCondition(group)}
                  disabled={isSavingAll || isLoadingConfigs}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Filter
                </Button>
                <div className="ml-auto flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => moveGroup(index, -1)}
                    disabled={index === 0 || isSavingAll}
                  >
                    <ArrowUp className="h-4 w-4" />
                    <span className="sr-only">Move group up</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => moveGroup(index, 1)}
                    disabled={index === groups.length - 1 || isSavingAll}
                  >
                    <ArrowDown className="h-4 w-4" />
                    <span className="sr-only">Move group down</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => removeGroup(group.groupId)}
                    disabled={groups.length === 1 || isSavingAll}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Remove group</span>
                  </Button>
                </div>
              </div>

              {shadowedBy && (
                <div className="mb-4 flex items-center gap-2 rounded-md border border-[var(--amber-6)] bg-[var(--amber-2)] px-3 py-2 text-sm text-[var(--amber-11)]">
                  <AlertTriangle size={16} />
                  Unreachable: every conversation matching this group is caught
                  first by Priority {groups.indexOf(shadowedBy) + 1}. Move this
                  group above it or narrow that group&apos;s filters.
                </div>
              )}

              {group.conditions.length > 0 && (
                <div className="mb-4 space-y-2">
                  <div className="text-sm text-[var(--gray-11)]">
                    Only conversations matching <strong>all</strong> of the
                    following are eligible for this group:
                  </div>
                  {group.conditions.map((condition) => {
                    const kind = getFieldKind(condition.field);
                    return (
                      <div
                        key={condition.id}
                        className="flex items-center gap-2"
                      >
                        <div className="w-[200px]">
                          <SharedSelect
                            options={AUDIENCE_FIELDS.map((f) => ({
                              value: f.value,
                              label: f.label,
                            }))}
                            value={condition.field}
                            onChange={(value) =>
                              updateCondition(group, condition.id, {
                                field: value,
                                operator:
                                  OPERATORS_BY_KIND[getFieldKind(value)][0]
                                    .value,
                                value:
                                  getFieldKind(value) === "boolean"
                                    ? "true"
                                    : "",
                              })
                            }
                            placeholder="Select field"
                          />
                        </div>
                        {kind === "boolean" ? (
                          <span className="text-sm text-[var(--gray-11)]">
                            is
                          </span>
                        ) : (
                          <div className="w-[200px]">
                            <SharedSelect
                              options={OPERATORS_BY_KIND[kind]}
                              value={condition.operator}
                              onChange={(value) =>
                                updateCondition(group, condition.id, {
                                  operator: value as AudienceOperator,
                                })
                              }
                              placeholder="Select operator"
                            />
                          </div>
                        )}
                        <div className="min-w-[160px] flex-1">
                          {kind === "boolean" ? (
                            <SharedSelect
                              options={BOOLEAN_OPTIONS}
                              value={condition.value}
                              onChange={(value) =>
                                updateCondition(group, condition.id, { value })
                              }
                              placeholder="Select value"
                            />
                          ) : kind === "number" ? (
                            <Input
                              type="number"
                              value={condition.value}
                              placeholder="Enter amount"
                              aria-invalid={isInvalidNumberCondition(condition)}
                              onChange={(e) =>
                                updateCondition(group, condition.id, {
                                  value: e.target.value,
                                })
                              }
                              className="w-full"
                            />
                          ) : (
                            <Input
                              value={condition.value}
                              placeholder="Enter value"
                              onChange={(e) =>
                                updateCondition(group, condition.id, {
                                  value: e.target.value,
                                })
                              }
                              className="w-full"
                            />
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => removeCondition(group, condition.id)}
                        >
                          <X className="h-4 w-4" />
                          <span className="sr-only">Remove filter</span>
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className={styles.tableContainer}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Topic</TableHead>
                      <TableHead>Quack</TableHead>
                      <TableHead>Apollo</TableHead>
                      <TableHead style={{ minWidth: 240 }}>
                        Apollo Traffic %
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedTopics.map((topic) => {
                      const apollo = group.percentages[topic.id] ?? 0;
                      const quack = 100 - apollo;

                      return (
                        <TableRow key={topic.id}>
                          <TableCell>{topic.topic}</TableCell>
                          <TableCell>
                            <Text small>{quack}%</Text>
                          </TableCell>
                          <TableCell>
                            <Text small>{apollo}%</Text>
                          </TableCell>
                          <TableCell>
                            <Flex
                              align="center"
                              gap="3"
                              style={{ minWidth: 0, width: "100%" }}
                            >
                              <Slider
                                value={[apollo]}
                                min={0}
                                max={100}
                                step={1}
                                onValueChange={(values) =>
                                  handlePercentageChange(
                                    group.groupId,
                                    topic.id,
                                    values[0] ?? 0
                                  )
                                }
                                disabled={isSavingAll || isLoadingConfigs}
                                style={{ flex: 1, minWidth: 0 }}
                              />
                              <Input
                                type="number"
                                min={0}
                                max={100}
                                value={apollo}
                                onChange={(e) =>
                                  handlePercentageChange(
                                    group.groupId,
                                    topic.id,
                                    Number(e.target.value)
                                  )
                                }
                                disabled={isSavingAll || isLoadingConfigs}
                                style={{ width: 64, flexShrink: 0 }}
                              />
                            </Flex>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
            );
          })}

          <Button
            variant="outline"
            className={styles.actionButton}
            onClick={addGroup}
            disabled={isSavingAll || isLoadingConfigs}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Filter Group
          </Button>
        </div>

        <Flex justify="end" gap="2" className={styles.rolloutFooter}>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSavingAll}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAll}
            disabled={
              isSavingAll ||
              isLoadingConfigs ||
              hasInvalidNumberCondition ||
              (changedRows.length === 0 && removedGroupIds.length === 0)
            }
          >
            {isSavingAll ? "Saving..." : "Save"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
